/**
 * Public series page showing essays in a series.
 * 
 * Features:
 * - Series header with description
 * - Ordered list of essays in the series
 * - Progress through the series
 * 
 * Called by:
 * - App.tsx as /series/:slug route
 */
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Typography, Spin, Alert, Card, Space, Breadcrumb } from 'antd';
import { CalendarOutlined, ClockCircleOutlined, HomeOutlined, OrderedListOutlined } from '@ant-design/icons';
import { getPublicSeriesDetail } from '../../services/api';
import { format } from 'date-fns';

const { Title, Text, Paragraph } = Typography;

/**
 * Public series page component.
 */
function PublicSeriesPage() {
  const { slug } = useParams<{ slug: string }>();

  // Fetch series with essays
  const {
    data: series,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['public', 'series', slug],
    queryFn: () => getPublicSeriesDetail(slug!),
    enabled: !!slug,
  });

  // Loading state
  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        <Spin size="large" />
      </div>
    );
  }

  // Error state
  if (error || !series) {
    return (
      <Alert
        message="Series not found"
        description="The series you're looking for doesn't exist."
        type="error"
        showIcon
      />
    );
  }

  return (
    <div>
      {/* Breadcrumb */}
      <Breadcrumb
        style={{ marginBottom: '24px' }}
        items={[
          { title: <Link to="/"><HomeOutlined /></Link> },
          { title: 'Series' },
          { title: series.name },
        ]}
      />

      {/* Series header */}
      <div style={{ marginBottom: '48px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <OrderedListOutlined style={{ fontSize: '1.5rem', color: '#d4af37' }} />
          <Title
            style={{
              fontSize: '2.5rem',
              fontFamily: "var(--font-serif)",
              fontWeight: 400,
              color: '#f5f5f5',
              margin: 0,
            }}
          >
            {series.name}
          </Title>
        </div>
        {series.description && (
          <Paragraph
            style={{
              color: '#a0a0a0',
              fontSize: '1.1rem',
              marginTop: '16px',
              maxWidth: '800px',
            }}
          >
            {series.description}
          </Paragraph>
        )}
        <Text type="secondary">
          {series.essays.length} {series.essays.length === 1 ? 'essay' : 'essays'} in this series
        </Text>
      </div>

      {/* Empty state */}
      {series.essays.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <Text type="secondary">No published essays in this series yet.</Text>
        </div>
      )}

      {/* Essays list */}
      {series.essays.length > 0 && (
        <div style={{ maxWidth: '800px' }}>
          {series.essays.map(({ essay }, index) => (
            <Link
              key={essay.id}
              to={`/essays/${essay.slug}`}
              style={{ textDecoration: 'none', display: 'block', marginBottom: '16px' }}
            >
              <Card
                hoverable
                style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  borderRadius: '12px',
                  transition: 'all 0.3s ease',
                }}
                bodyStyle={{ padding: '24px' }}
                className="essay-card"
              >
                <div style={{ display: 'flex', gap: '24px' }}>
                  {/* Part number */}
                  <div
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      background: 'rgba(212, 175, 55, 0.15)',
                      border: '2px solid #d4af37',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Text
                      style={{
                        color: '#d4af37',
                        fontSize: '1.2rem',
                        fontWeight: 600,
                      }}
                    >
                      {index + 1}
                    </Text>
                  </div>

                  {/* Essay info */}
                  <div style={{ flex: 1 }}>
                    <Title
                      level={4}
                      style={{
                        color: '#f5f5f5',
                        marginBottom: '8px',
                        fontFamily: "var(--font-serif)",
                      }}
                    >
                      {essay.title}
                    </Title>

                    <Paragraph
                      ellipsis={{ rows: 2 }}
                      style={{
                        color: '#a0a0a0',
                        fontSize: '0.95rem',
                        lineHeight: 1.7,
                        marginBottom: '12px',
                      }}
                    >
                      {essay.preview}
                    </Paragraph>

                    <Space size="middle" style={{ color: '#666' }}>
                      {(essay as any).publishedAt && (
                        <span>
                          <CalendarOutlined style={{ marginRight: '4px' }} />
                          {format(new Date((essay as any).publishedAt), 'MMM d, yyyy')}
                        </span>
                      )}
                      {(essay as any).readingTime && (
                        <span>
                          <ClockCircleOutlined style={{ marginRight: '4px' }} />
                          {(essay as any).readingTime} min read
                        </span>
                      )}
                    </Space>
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}

      <style>{`
        .essay-card:hover {
          border-color: rgba(212, 175, 55, 0.3) !important;
          transform: translateY(-4px);
        }
      `}</style>
    </div>
  );
}

export default PublicSeriesPage;
